import styled, { css } from "styled-components";

import { PageSection } from "../../components/layout/layout-styles";
import { Heading as HeadingStyles } from "../../components/common/Typography/type-styles";
import {
  breakpoints,
  fontFamily,
  colors,
  sectionRhythm,
  spacing,
} from "../../../theme";

const pointerHover = (styles) => css`
  @media (hover: hover) and (pointer: fine) {
    ${styles}
  }
`;

export const Section = styled(PageSection)`
  background-color: ${(props) =>
    props.bgColor ? props.bgColor : colors.white};
`;

export const Heading = styled(HeadingStyles).attrs({ as: "h2" })``;

export const ProjectList = styled.div`
  box-sizing: border-box;
  width: 100%;
  max-width: 1040px;
  margin: 0 auto;
  margin-top: ${sectionRhythm.paddingY.mobile}px;
  padding: 0 20px;

  @media only screen and (min-width: ${sectionRhythm.breakpoint}) {
    margin-top: ${sectionRhythm.paddingY.desktop}px;
  }

  @media only screen and (min-width: ${breakpoints.desktop}) {
    padding: 0;
  }
`;

export const CardsStack = styled.div`
  display: flex;
  flex-direction: column;

  ${(props) =>
    props.$stacked &&
    css`
      gap: ${spacing(6)};

      @media only screen and (min-width: ${breakpoints.desktop}) {
        gap: ${spacing(8)};
      }
    `}
`;

export const SubCards = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing(6)};
  margin-top: ${spacing(6)};

  @media only screen and (min-width: ${breakpoints.desktop}) {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: ${spacing(5)};
    margin-top: ${spacing(8)};
  }
`;

export const MoreProjectsWrap = styled.div`
  display: flex;
  justify-content: center;
  margin-top: ${spacing(12)};
`;

const mainVariant = css`
  flex-direction: row;
  width: 100%;
  min-height: 180px;

  @media only screen and (min-width: ${breakpoints.desktop}) {
    flex-direction: row;
    min-height: 0;
    height: auto;
  }
`;

const defaultVariant = css`
  flex-direction: row;
  width: 100%;
  min-height: 180px;

  @media only screen and (min-width: ${breakpoints.desktop}) {
    flex-direction: column;
    width: 100%;
    min-height: 0;
    height: auto;
  }
`;

const compactVariant = css`
  flex-direction: row;
  width: 100%;
  min-height: 120px;
`;

export const ProjectCardRoot = styled.div`
  position: relative;
  display: flex;
  overflow: hidden;
  isolation: isolate;
  background-color: ${(props) => props.$bgColor};
  border-radius: 20px;
  transition: background-color 0.3s ease, box-shadow 0.3s ease;
  box-shadow: ${colors.shadow.card};
  ${(props) =>
    props.$accent &&
    css`
      --project-card-accent: ${props.$accent};
    `}

  ${(props) => {
    if (props.$variant === "main") return mainVariant;
    if (props.$variant === "compact") return compactVariant;
    return defaultVariant;
  }}

  ${(props) =>
    pointerHover(css`
      &:hover {
        background-color: ${props.$hoverBgColor || props.$bgColor};
        box-shadow: 17px 17px 56px 0 rgb(0 0 0 / 16%);
      }
    `)}
`;

export const CardOverlayLink = styled.a`
  position: absolute;
  inset: 0;
  z-index: 10;
  border-radius: 20px;

  ${(props) =>
    props.$hideOnDesktop &&
    css`
      @media only screen and (min-width: ${breakpoints.desktop}) {
        display: none;
      }
    `}
`;

const mainFigure = css`
  position: relative;
  width: 35%;
  min-width: 35%;
  flex-shrink: 0;
  align-self: stretch;

  @media only screen and (min-width: ${breakpoints.desktop}) {
    height: 408px;
    min-height: 408px;
    width: 50%;
    min-width: 50%;
    align-self: auto;
  }
`;

const defaultFigure = css`
  position: relative;
  width: 35%;
  min-width: 35%;
  flex-shrink: 0;
  align-self: stretch;

  @media only screen and (min-width: ${breakpoints.desktop}) {
    width: 100%;
    min-width: 0;
    height: 250px;
    min-height: 250px;
  }
`;

const compactFigure = css`
  position: relative;
  width: 30%;
  min-width: 30%;
  flex-shrink: 0;
  align-self: stretch;
`;

export const CardFigure = styled.figure`
  margin: 0;
  overflow: hidden;
  ${(props) => {
    if (props.$variant === "main") return mainFigure;
    if (props.$variant === "compact") return compactFigure;
    return defaultFigure;
  }}

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;

    ${(props) =>
      (props.$variant === "default" ||
        props.$variant === "compact" ||
        props.$variant === "main") &&
      css`
        position: absolute;
        inset: 0;

        ${props.$variant === "main" &&
        css`
          @media only screen and (min-width: ${breakpoints.desktop}) {
            position: static;
            inset: auto;
          }
        `}
      `}
  }

  ${pointerHover(css`
    ${ProjectCardRoot}:hover & img {
      transform: scale(1.05);
    }
  `)}
`;

const mainBody = css`
  flex: 1;
  min-width: 0;
  padding: ${spacing(3)} ${spacing(3)} 9px;

  @media only screen and (min-width: ${breakpoints.desktop}) {
    padding: ${spacing(8)};
  }
`;

const defaultBody = css`
  flex: 1;
  min-width: 0;
  padding: ${spacing(3)} ${spacing(3)} 9px;

  @media only screen and (min-width: ${breakpoints.desktop}) {
    padding: 20px 27px;
  }
`;

const compactBody = css`
  flex: 1;
  min-width: 0;
  padding: ${spacing(3)};
  gap: ${spacing(2)};
`;

export const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing(4)};
  ${(props) => {
    if (props.$variant === "main") return mainBody;
    if (props.$variant === "compact") return compactBody;
    return defaultBody;
  }}
`;

export const TitleStack = styled.div`
  display: flex;
  flex-direction: column-reverse;

  @media only screen and (min-width: ${breakpoints.desktop}) {
    flex-direction: ${(props) =>
      props.$variant === "compact" ? "column-reverse" : "column"};
  }
`;

export const ClientName = styled.p`
  margin: 0;
  font-family: ${fontFamily.bold};
  font-size: 16px;
  line-height: 1.2;
  color: ${(props) => props.$accent};
  transition: color 0.3s ease;

  ${(props) =>
    props.$variant === "main" &&
    css`
      @media only screen and (min-width: ${breakpoints.desktop}) {
        font-size: 20px;
      }
    `}

  ${(props) =>
    props.$variant === "compact" &&
    css`
      font-size: 12px;

      @media only screen and (min-width: ${breakpoints.desktop}) {
        font-size: 16px;
      }
    `}

  ${(props) =>
    props.$hoverAccent &&
    pointerHover(css`
      ${ProjectCardRoot}:hover & {
        color: var(--project-card-accent);
      }
    `)}
`;

export const ProjectTitle = styled.h3`
  margin: 0;
  font-family: ${fontFamily.bold};
  line-height: 102%;
  color: ${colors.grey.dark};

  ${(props) => {
    if (props.$variant === "main") {
      return css`
        font-size: 18px;

        @media only screen and (min-width: ${breakpoints.desktop}) {
          font-size: 32px;
        }
      `;
    }

    if (props.$variant === "compact") {
      return css`
        font-size: 12px;

        @media only screen and (min-width: ${breakpoints.desktop}) {
          font-size: 18px;
        }
      `;
    }

    return css`
      font-size: 18px;

      @media only screen and (min-width: ${breakpoints.desktop}) {
        font-size: 20px;
      }
    `;
  }}
`;

export const BadgeRow = styled.div`
  position: relative;
  z-index: 20;
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing(2)};
  margin-top: auto;

  @media only screen and (min-width: ${breakpoints.desktop}) {
    margin-top: 0;
  }
`;

export const TechBadge = styled.span`
  position: relative;
  z-index: ${(props) => (props.$interactive ? 20 : "auto")};
  display: inline-flex;
  align-items: center;
  padding: 4px 16px;
  border-radius: 20px;
  border: 2px solid ${(props) => props.$border};
  color: ${(props) => props.$color};
  font-family: ${fontFamily.medium};
  font-size: 12px;
  line-height: 153%;
  text-decoration: none;
  transition: background-color 0.2s ease, border-color 0.2s ease;

  ${(props) =>
    props.$interactive &&
    css`
      cursor: pointer;

      ${pointerHover(css`
        &:hover {
          background-color: ${colors.white};
        }
      `)}

      &:focus-visible {
        outline: 2px solid ${props.$color};
        outline-offset: 2px;
      }
    `}
`;

export const CircularCta = styled.a`
  position: relative;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${(props) => (props.$compact ? "48px" : "40px")};
  height: ${(props) => (props.$compact ? "48px" : "40px")};
  margin-top: auto;
  margin-left: auto;
  border-radius: 50%;
  background-color: ${(props) => props.$bg};
  color: ${colors.grey.dark};
  text-decoration: none;
  flex-shrink: 0;

  ${(props) =>
    !props.$compact &&
    css`
      @media only screen and (min-width: ${breakpoints.desktop}) {
        display: none;
      }
    `}

  ${(props) =>
    props.$compact &&
    css`
      opacity: 0;
      pointer-events: none;
      transition: opacity 0.3s ease;

      ${pointerHover(css`
        ${ProjectCardRoot}:hover & {
          opacity: 1;
          pointer-events: auto;
        }
      `)}

      ${ProjectCardRoot}:focus-within & {
        opacity: 1;
        pointer-events: auto;
      }
    `}

  svg {
    width: ${(props) => (props.$compact ? "24px" : "20px")};
    height: ${(props) => (props.$compact ? "24px" : "20px")};
    transition: transform 0.3s ease-out;
  }

  ${pointerHover(css`
    ${ProjectCardRoot}:hover & svg {
      transform: rotate(-45deg);
    }
  `)}

  ${ProjectCardRoot}:active & svg {
    transform: rotate(0deg);
  }
`;

export const CaseStudyButtonWrap = styled.div`
  position: relative;
  z-index: 20;
  display: none;
  margin-top: auto;
  align-self: flex-start;

  @media only screen and (min-width: ${breakpoints.desktop}) {
    display: block;
  }
`;
